import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MailX, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [status, setStatus] = useState('pending'); // pending, success, error
  const [loading, setLoading] = useState(false);

  const handleUnsubscribe = async () => {
    if (!email) {
      setStatus('error');
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/subscribers/${encodeURIComponent(email)}`);
      setStatus('success');
    } catch (error) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 flex items-center justify-center px-6" data-testid="unsubscribe-page">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-3xl text-stone-900 mb-4">Invalid Link</h1>
          <p className="text-stone-600 mb-8">This unsubscribe link is invalid or expired.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-red-800 hover:text-red-900"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-32 flex items-center justify-center px-6" data-testid="unsubscribe-page">
      <div className="max-w-md text-center">
        {status === 'pending' && (
          <>
            <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-8">
              <MailX size={40} className="text-stone-600" />
            </div>
            <h1 className="font-serif text-3xl text-stone-900 mb-4">Unsubscribe</h1>
            <p className="text-stone-600 mb-2">
              Are you sure you want to unsubscribe?
            </p>
            <p className="text-stone-500 text-sm mb-8">
              Email: {email}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="bg-red-800 text-stone-50 px-8 py-3 rounded-sm hover:bg-red-900 transition-colors disabled:opacity-50"
                data-testid="confirm-unsubscribe-btn"
              >
                {loading ? 'Processing...' : 'Yes, Unsubscribe'}
              </button>
              <Link
                to="/"
                className="bg-stone-200 text-stone-900 px-8 py-3 rounded-sm hover:bg-stone-300 transition-colors"
              >
                Cancel
              </Link>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h1 className="font-serif text-3xl text-stone-900 mb-4">Unsubscribed</h1>
            <p className="text-stone-600 mb-8">
              You've been successfully unsubscribed from our newsletter.
              We're sad to see you go!
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-red-800 hover:text-red-900"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-8">
              <MailX size={40} className="text-red-600" />
            </div>
            <h1 className="font-serif text-3xl text-stone-900 mb-4">Something Went Wrong</h1>
            <p className="text-stone-600 mb-8">
              We couldn't process your request. The email may not be subscribed or there was an error.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-red-800 hover:text-red-900"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
